/**
 * "Backed by Doctors" evidence-based methodology modal.
 *
 * Explains the scientific factors behind the sunscreen recommendation algorithm
 * (UV dose integration, MED thresholds, cloud cover, surface reflection, altitude)
 * and cites peer-reviewed dermatological research papers with links.
 */
import React from "react";
import "./DoctorBackedModal.css";

const DoctorBackedModal = ({ isOpen, onClose, uvIndex, isDark }) => {
  if (!isOpen) return null;

  const factors = [
    {
      title: "UV Index Measurement",
      description: "Real-time UV radiation levels from your exact location, updated hourly to reflect current atmospheric conditions."
    },
    {
      title: "Skin Type Classification",
      description: "Based on the scientifically-validated Fitzpatrick scale, which categorizes skin's response to UV exposure across six phototypes."
    },
    {
      title: "UV Dose Integration",
      description: "Calculates cumulative UV exposure over your planned outdoor duration using an advanced integration algorithm that accounts for changing UV levels throughout the day."
    },
    {
      title: "Minimal Erythema Dose (MED)",
      description: "Determines the UV radiation threshold that causes sunburn for your specific skin type, derived from dermatological research."
    },
    {
      title: "Cloud Cover Effects",
      description: "Adjusts UV exposure based on cloud density, accounting for both UV attenuation and scattering effects from atmospheric conditions."
    },
    {
      title: "Surface Reflection",
      description: "Incorporates UV albedo from surrounding surfaces like snow, sand, water, and grass, which can significantly increase total UV exposure."
    },
    {
      title: "Altitude Adjustment",
      description: "Accounts for increased UV intensity at higher elevations, where the thinner atmosphere provides less UV protection."
    },
    {
      title: "Time-Weighted Analysis",
      description: "Considers the specific time of day and duration of outdoor exposure, factoring in the sun's angle and intensity variations."
    }
  ];

  const papers = [
    {
      title: "UV radiation and the skin",
      journal: "Photochemical & Photobiological Sciences",
      year: "2018",
      url: "https://pubs.rsc.org/en/content/articlehtml/2018/pp/c7pp00374a"
    },
    {
      title: "Fitzpatrick Skin Type and Minimal Erythema Dose",
      journal: "eScholarship University of California",
      url: "https://escholarship.org/uc/item/5925w4hq"
    },
    {
      title: "Sunburn Protection Factor (SPF): Mathematics and In Vitro Methods",
      journal: "PMC - National Center for Biotechnology Information",
      year: "2018",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6069363/"
    },
    {
      title: "Spectral UV Albedo for Environmental Surfaces",
      journal: "ResearchGate",
      url: "https://www.researchgate.net/figure/Spectral-UV-albedo-for-a-sand-b-earth-c-grass-and-d-snow-The-data-has-been_fig1_326469577"
    },
    {
      title: "UV Exposure and Protection Against Skin Damage",
      journal: "PMC - National Center for Biotechnology Information",
      year: "2019",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6736991/"
    }
  ];

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="doctor-modal-container">
        <button className="modal-close" onClick={onClose}>
          ✕
        </button>
        
        <h2 className="doctor-modal-title">Evidence-based UV protection</h2>
        
        <div className="doctor-modal-content">
          <p className="doctor-modal-intro">
            Our sunscreen recommendations are powered by a sophisticated algorithm that integrates multiple scientific factors to provide personalized UV protection guidance.
          </p>

          <h3 className="doctor-modal-section-title">Algorithm factors</h3>
          <div className="factors-grid">
            {factors.map((factor, index) => (
              <div key={index} className="factor-item">
                <h4 className="factor-title">{factor.title}</h4>
                <p className="factor-description">{factor.description}</p>
              </div>
            ))}
          </div>

          <h3 className="doctor-modal-section-title">Scientific research</h3>
          <p className="research-intro">
            Our methodology is based on peer-reviewed research from leading dermatological and photobiological journals:
          </p>
          <div className="papers-list">
            {papers.map((paper, index) => (
              <div key={index} className="paper-item">
                <a 
                  href={paper.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="paper-link"
                >
                  {paper.title}
                </a>
                {paper.journal && (
                  <span className="paper-journal"> - {paper.journal}</span>
                )}
                {paper.year && (
                  <span className="paper-year"> ({paper.year})</span>
                )}
              </div>
            ))}
          </div>

          <div className="doctor-modal-footer">
            <p className="disclaimer">
              This app provides general guidance based on scientific research. Always consult with healthcare professionals for personalized medical advice.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DoctorBackedModal;